import { useAsyncError } from '@/context/async-error-boundary';
import { useAuthContext } from '@/context/auth-provider';
import { useMutationWithErrorBoundary } from '@/hooks/use-mutation-with-error';
import { toast } from '@/hooks/use-toast';
import { changeRecoveryEmailMutationFn } from '@/lib/api';
import { useZodForm } from '@/lib/zod';
import { OnlyEmailSchema } from '@/schemas';
import { TRecoverEmail } from '@/types/types';
import { useQueryClient } from '@tanstack/react-query';
import { LoaderCircleIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormSuccess } from '../form-success';
import FormattedMessage from '../formatted-message';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription } from '../ui/card';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';

export default function RecoveryEmailCard() {
    const { user } = useAuthContext();
    const [responseSuccess, setResponseSuccess] = useState('');
    const [recoveryEmail, setRecoveryEmail] = useState(
        user.security.recoveryEmail || '',
    );
    const [isVisible, setIsVisible] = useState(false);
    const queryClient = useQueryClient();
    const { captureError } = useAsyncError();

    const recoveryEmailForm = useZodForm(OnlyEmailSchema, {
        defaultValues: {
            email: user.security.recoveryEmail ?? undefined,
        },
    });

    const { mutate, isPending } = useMutationWithErrorBoundary({
        mutationFn: changeRecoveryEmailMutationFn,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['authUser'],
                stale: true,
                refetchType: 'all',
            });
            setTimeout(() => {
                setIsVisible(false);
            }, 5000);
            setTimeout(() => {
                setResponseSuccess('');
            }, 5300);
        },
    });
    const recoveryEmailSubmit = (values: TRecoverEmail) => {
        try {
            setIsVisible(true);
            mutate(
                { ...values, userId: user.id },
                {
                    onSuccess: (response) => {
                        setRecoveryEmail(response.data.recoveryEmail);
                        setResponseSuccess(response.data.message);
                        recoveryEmailForm.reset();
                        toast({
                            title: 'Recovery Email',
                            description: (
                                <FormattedMessage
                                    message={response.data.message}
                                />
                            ),
                            className: 'text-white',
                            variant: 'success',
                        });
                    },
                    onError: (error) => {
                        toast({
                            title: 'Error',
                            description: (
                                <FormattedMessage message={error.message} />
                            ),
                            variant: 'destructive',
                        });
                    },
                },
            );
        } catch (error) {
            captureError(error as Error);
        }
    };

    useEffect(() => {
        recoveryEmailForm.reset({
            email: user.security.recoveryEmail ?? undefined,
        });
    }, [recoveryEmail, recoveryEmailForm, user.security.recoveryEmail]);

    return (
        <Card className='w-full border-none bg-accent/50'>
            <CardContent className='space-y-4 p-2 md:p-6'>
                <Form {...recoveryEmailForm}>
                    <form
                        noValidate
                        onSubmit={recoveryEmailForm.handleSubmit(
                            recoveryEmailSubmit,
                        )}>
                        <FormField
                            control={recoveryEmailForm.control}
                            name='email'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Recovery Email</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            placeholder='user@example.com'
                                            disabled={isPending}
                                        />
                                    </FormControl>
                                    <FormMessage />

                                    <FormSuccess
                                        message={responseSuccess}
                                        classes={`transition-opacity duration-300 ${isVisible ? 'opacity-1' : 'opacity-0'}`}
                                    />
                                </FormItem>
                            )}
                        />
                        <Button
                            className='my-4 w-full sm:w-auto'
                            disabled={
                                isPending ||
                                !recoveryEmailForm.getFieldState('email')
                                    .isDirty ||
                                (!recoveryEmailForm.watch().email &&
                                    !user.security.recoveryEmail)
                            }>
                            {isPending ? (
                                <>
                                    <LoaderCircleIcon
                                        size={5}
                                        className='animate-spin'
                                    />
                                    Sending...
                                </>
                            ) : (
                                'Save'
                            )}
                        </Button>
                    </form>
                </Form>

                <CardDescription className='pb-4 md:pb-0 text-sm'>
                    <span className='font-bold text-destructive'>* </span>
                    Recovery email should be different from your primary email.
                </CardDescription>
            </CardContent>
        </Card>
    );
}
